import { useCallback, useEffect, useRef } from 'react';
import { overlay } from 'overlay-kit';
import { useGetMyProfile } from '@/api/gen/quiz-user/quiz-user.gen';
import { useUser } from '@/lib/auth';
import { CookieManager } from '@/shared/cookies';
import { markPromotionModalShown, wasPromotionModalShown } from '@/shared/features/promotion-modal-state';
import { PhoneVerificationBannerModal } from '../components/PhoneVerificationBannerModal';
export const PHONE_VERIFICATION_BANNER_COOKIE = 'phone_verification_banner_dismissed';

/**
 * 휴대폰 인증 배너 모달을 관리하는 훅
 *
 * 다음 조건을 모두 만족할 때 모달을 표시합니다:
 * 1. 로그인한 사용자
 * 2. 휴대폰 인증을 하지 않은 사용자
 * 3. "오늘 하루동안 보지 않기" 쿠키가 설정되지 않은 경우
 * 4. 학생 계정이 아닌 경우
 * 5. KR 국가 사용자인 경우
 */
export const usePhoneVerificationBannerModal = () => {
  const {
    user,
    isAuthenticated,
    isInitialized
  } = useUser();
  const {
    data: userProfile,
    isLoading: isProfileLoading
  } = useGetMyProfile({
    query: {
      enabled: isAuthenticated
    }
  });
  const hasOpenedRef = useRef(false);
  const isDismissedForToday = useCallback(() => {
    const cookieManager = new CookieManager(PHONE_VERIFICATION_BANNER_COOKIE);
    return cookieManager.has();
  }, []);
  const dismissForToday = useCallback(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const cookieManager = new CookieManager(PHONE_VERIFICATION_BANNER_COOKIE, {
      expires: tomorrow
    });
    cookieManager.set('true');
  }, []);
  const openModal = useCallback(() => {
    overlay.open(({
      close,
      isOpen
    }) => <PhoneVerificationBannerModal isOpen={isOpen} onClose={close} onDismissForToday={dismissForToday} />);
  }, [dismissForToday]);
  const shouldShowModal = useCallback(() => {
    if (!isInitialized) return false;
    if (!isAuthenticated || !user) return false;
    if (isProfileLoading) return false;
    if (userProfile?.data.verifiedPhoneNumber) return false;
    if (user.type?.toLowerCase() === 'student') return false;
    if (user.countryCode && user.countryCode !== 'KR') return false;
    if (isDismissedForToday()) return false;
    if (wasPromotionModalShown()) return false;
    return true;
  }, [isInitialized, isAuthenticated, isProfileLoading, user, userProfile, isDismissedForToday]);
  useEffect(() => {
    if (hasOpenedRef.current) return;
    if (shouldShowModal()) {
      hasOpenedRef.current = true;
      markPromotionModalShown();
      openModal();
    }
  }, [shouldShowModal, openModal]);
  return {
    openModal,
    dismissForToday,
    isDismissedForToday
  };
};